import { Injectable, SecurityContext, inject } from '@angular/core';
import { catchError, map, Observable, tap } from 'rxjs';
import { RequestBuilderService } from './request-builder.service';
import { DomSanitizer } from '@angular/platform-browser';
import { EnvConfigService } from './env-config.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly env = inject(EnvConfigService);

  private loaded = false;

  loadGoogleMaps(): Observable<void> {
    return new Observable(observer => {
      if (this.loaded) {
        observer.next();
        observer.complete();
        return;
      }

      // Check if the script is already in the document to avoid duplicates
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        this.loaded = true;
        observer.next();
        observer.complete();
        return;
      }

      // Fetch the API key
      this.requestBuilder
        .get(`configurations/googleMapsApiKey`)
        .pipe(
          catchError(err => {
            observer.error(new Error('Failed to fetch API key: ' + err.message));
            return [];
          }),
          map((res: any) => res.data.propertyValue),
          tap((apiKey: string) => {
            const sanitizedApiKey = this.sanitizer.sanitize(SecurityContext.NONE, apiKey);
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${sanitizedApiKey}&libraries=geometry,marker`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
              this.loaded = true;
              if (typeof google !== 'undefined') {
                observer.next();
                observer.complete();
              } else {
                observer.error(new Error('Google Maps API not loaded properly.'));
              }
            };

            script.onerror = error => {
              observer.error(error);
            };

            if (this.env.getConfig().isRunAsLibrary) {
              observer.next();
              observer.complete();
            } else {
              document.head.appendChild(script);
            }
          }),
        )
        .subscribe();
    });
  }
}
